/*
 * FunnelDropOff.js — Phase 4 biggest-drop-off callout for the Marketing tab.
 *
 * Consumes the existing /api/google-analytics/clients/{id}/funnel endpoint,
 * finds the stage with the largest drop_off_pct_from_prev, and computes an
 * "estimated lost revenue" using: (users_lost × client's average AOV).
 *
 * Renders a compact card with the funnel visualisation + the AI-style
 * callout arrow pointing at the biggest drop.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { CaretDown, TrendDown, Warning } from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmtNum = (v, compact = true) => {
  if (v == null || isNaN(v)) return "—";
  if (compact && Math.abs(v) >= 1000) {
    return new Intl.NumberFormat("en-IN", { notation: "compact", maximumFractionDigits: 1 }).format(v);
  }
  return new Intl.NumberFormat("en-IN").format(v);
};
const fmtMoney = (v, ccy = "INR") => {
  if (v == null || isNaN(v)) return "—";
  try {
    return new Intl.NumberFormat("en-IN", { style: "currency", currency: ccy, maximumFractionDigits: 0, notation: "compact" }).format(v);
  } catch {
    return `${ccy} ${Math.round(v).toLocaleString("en-IN")}`;
  }
};

const FunnelDropOff = ({ clientId, dateRange, currency = "INR", avgOrderValue = 0 }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!clientId || !dateRange) return;
    let cancelled = false;
    setLoading(true);
    setErr(null);
    axios.get(`${API}/google-analytics/clients/${clientId}/funnel`, {
      params: { start_date: dateRange.start_date, end_date: dateRange.end_date },
    }).then((res) => { if (!cancelled) setData(res.data); })
      .catch((e) => { if (!cancelled) setErr(e.response?.data?.detail || e.message || "Funnel unavailable"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [clientId, dateRange?.start_date, dateRange?.end_date]);

  if (loading) {
    return (
      <div className="p-4 rounded-xl bg-[#0B0B0F] border border-white/10" data-testid="funnel-dropoff-loading">
        <div className="h-3 bg-white/10 rounded w-32 mb-3 animate-pulse" />
        <div className="h-24 bg-white/5 rounded animate-pulse" />
      </div>
    );
  }

  if (err || !data?.steps?.length) {
    return null; // silent skip when GA4 not connected
  }

  const steps = data.steps;
  const totalSteps = steps.length;

  // Find the biggest drop-off (skip the first step which has no prev)
  let biggest = { idx: -1, pct: 0, users_lost: 0, from: null, to: null };
  for (let i = 1; i < steps.length; i++) {
    const s = steps[i];
    const prev = steps[i - 1];
    const pct = Number(s.drop_off_pct_from_prev) || 0;
    if (pct > biggest.pct) {
      const usersLost = Math.max(0, (Number(prev.users) || 0) - (Number(s.users) || 0));
      biggest = { idx: i, pct, users_lost: usersLost, from: prev, to: s };
    }
  }

  // Estimated lost revenue: assume the users we lost would convert at the
  // rate of the tail of the funnel (users_at_last / users_at_biggest) and
  // spend one AOV each.
  const tailConvRate = biggest.idx >= 0 && biggest.to
    ? (Number(steps[totalSteps - 1].users) || 0) / Math.max(1, Number(biggest.to.users) || 1)
    : 0;
  const estLostRevenue = biggest.users_lost * tailConvRate * (Number(avgOrderValue) || 0);

  const maxUsers = Math.max(...steps.map(s => Number(s.users) || 0), 1);

  return (
    <div className="p-4 rounded-xl bg-[#0B0B0F] border border-white/10 space-y-3" data-testid="funnel-dropoff">
      <div className="flex items-baseline justify-between">
        <div>
          <h4 className="text-sm font-semibold text-white flex items-center gap-2" style={{ fontFamily: "Varela Round, sans-serif" }}>
            <TrendDown size={14} weight="fill" className="text-red-400" /> Funnel Intelligence
          </h4>
          <p className="text-[10px] text-white/50 mt-0.5">GA4 session-to-purchase — biggest drop-off highlighted</p>
        </div>
        <span className="text-[10px] text-white/40 uppercase tracking-wider">
          {dateRange.start_date} → {dateRange.end_date}
        </span>
      </div>

      {/* Funnel bars */}
      <div className="space-y-1.5" data-testid="funnel-dropoff-steps">
        {steps.map((s, i) => {
          const w = Math.max(((Number(s.users) || 0) / maxUsers) * 100, 2);
          const isBiggest = i === biggest.idx;
          const dropPct = Number(s.drop_off_pct_from_prev) || null;
          return (
            <div key={s.event || i} className="grid grid-cols-[130px_1fr_60px] gap-2 items-center" data-testid={`funnel-step-${i}`}>
              <div className={`text-[11px] truncate ${isBiggest ? "text-red-300 font-semibold" : "text-white/70"}`}>
                {s.event}
              </div>
              <div className="relative h-6 bg-white/[0.04] rounded overflow-hidden">
                <div
                  className={`h-full transition-all ${isBiggest ? "bg-red-500/60" : "bg-[#FFEB00]/60"}`}
                  style={{ width: `${w}%` }}
                />
                <div className="absolute inset-0 px-2 flex items-center text-[11px] font-mono text-white">
                  {fmtNum(s.users)} users
                </div>
              </div>
              <div className={`text-[11px] font-mono text-right ${
                dropPct == null ? "text-white/30" :
                dropPct > 50 ? "text-red-400 font-semibold" :
                dropPct > 20 ? "text-yellow-300" :
                "text-emerald-400"
              }`}>
                {dropPct != null ? `-${dropPct.toFixed(0)}%` : "—"}
              </div>
            </div>
          );
        })}
      </div>

      {/* Biggest drop-off AI callout */}
      {biggest.idx > 0 && biggest.pct > 0 && (
        <div className="mt-3 p-3 rounded-lg bg-red-500/8 border border-red-500/25" data-testid="funnel-biggest-callout">
          <div className="flex items-start gap-2.5">
            <Warning size={14} weight="fill" className="text-red-400 mt-0.5 shrink-0" />
            <div className="flex-1">
              <p className="text-[12px] text-white font-semibold leading-snug">
                Biggest drop: <span className="text-red-300">{biggest.from.event} → {biggest.to.event}</span> ({biggest.pct.toFixed(0)}%)
              </p>
              <p className="text-[11px] text-white/70 mt-1 leading-relaxed">
                {fmtNum(biggest.users_lost)} users dropped at this stage.{" "}
                {estLostRevenue > 0 && avgOrderValue > 0 && (
                  <>
                    Estimated lost revenue this window:{" "}
                    <strong className="text-[#FFEB00]">{fmtMoney(estLostRevenue, currency)}</strong>{" "}
                    <span className="text-white/40 text-[10px]">(users_lost × tail conv rate × AOV)</span>
                  </>
                )}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FunnelDropOff;
